import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { MainDisplay } from "./components/MainDisplay";
import { HeartDisplay, ThirdEyeDisplay } from "./components/AuxDisplays";
import { DeveloperPanel } from "./components/DeveloperPanel";
import { DISPLAY_ROLES, type DisplayRole, type Point, type WalterSnapshot } from "./core/types";
import { publicWalterController, walterController } from "./core/walterController";
import { walterAudio } from "./core/walterAudio";

function readDisplayRole(): DisplayRole {
  const requested = new URLSearchParams(window.location.search).get("display");
  return DISPLAY_ROLES.includes(requested as DisplayRole) ? requested as DisplayRole : "main";
}

function AudioObserver({ enabled }: { enabled: boolean }) {
  const snapshot = useSyncExternalStore(walterController.subscribe, walterController.getSnapshot);
  useEffect(() => { if (enabled) walterAudio.cueState(snapshot.state); }, [enabled, snapshot.state]);
  useEffect(() => { if (enabled) walterAudio.cueTool(snapshot.tool.phase); }, [enabled, snapshot.tool.nonce, snapshot.tool.phase]);
  return null;
}

function SignalBridge({ snapshot, bridge }: { snapshot: WalterSnapshot; bridge: "eye-face" | "face-heart" }) {
  const direction = snapshot.transfer?.direction ?? "none";
  const active = bridge === "eye-face"
    ? direction === "face-to-eye" || direction === "eye-to-face"
    : direction === "face-to-heart";
  const gradientId = `bridge-${bridge}-light`;
  return <div
    className={`signal-gap ${bridge}-gap direction-${direction} phase-${snapshot.transfer?.phase ?? "none"} ${active ? "is-active" : ""}`}
    aria-hidden="true"
  >
    <svg viewBox="0 0 60 88" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dfffff" stopOpacity="0" />
          <stop offset=".48" stopColor="#8cf7ff" stopOpacity=".82" />
          <stop offset="1" stopColor="#1bc7d7" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path className="bridge-vessel" d="M30 0C19 14 43 25 30 43C17 61 41 72 30 88" />
      <path className="bridge-current current-a" stroke={`url(#${gradientId})`} d="M30 0C19 14 43 25 30 43C17 61 41 72 30 88" />
      <path className="bridge-current current-b" stroke={`url(#${gradientId})`} d="M27 0C38 16 18 30 31 45C44 60 21 75 33 88" />
      <path className="bridge-current current-c" stroke={`url(#${gradientId})`} d="M33 0C22 18 42 31 29 47C16 63 39 75 27 88" />
    </svg>
    <i className="bridge-fold" />
  </div>;
}

export default function App() {
  const snapshot = useSyncExternalStore(walterController.subscribe, walterController.getSnapshot);
  const role = useMemo(readDisplayRole, []);
  const kiosk = new URLSearchParams(window.location.search).get("kiosk") === "1";
  const [fps, setFps] = useState(0);
  const [touch, setTouch] = useState<Point>({ x: 0, y: 0 });
  const [safeArea, setSafeArea] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [benchScale, setBenchScale] = useState<"fit" | "exact">("fit");
  const [benchView, setBenchView] = useState<"eye" | "face" | "heart" | "triptych">("face");

  useEffect(() => { window.walter = publicWalterController; }, []);

  const enableSound = async () => {
    if (soundEnabled) {
      walterAudio.disable();
      setSoundEnabled(false);
      return;
    }
    await walterAudio.enable();
    walterAudio.setVolume(snapshot.audioLevel);
    setSoundEnabled(true);
  };

  useEffect(() => {
    if (soundEnabled) walterAudio.setVolume(snapshot.audioLevel);
  }, [snapshot.audioLevel, soundEnabled]);

  const onFps = useCallback((value: number) => setFps(value), []);
  const onTouch = useCallback((value: Point) => setTouch(value), []);

  const main = <MainDisplay snapshot={snapshot} safeArea={safeArea} onFps={onFps} onTouch={onTouch} />;

  const previewAwakening = () => {
    walterController.sleep();
    window.setTimeout(() => walterController.wake(), 1100);
  };

  const previewBodySignal = () => {
    walterController.wake();
    walterController.triggerToolCall("vision");
    window.setTimeout(() => walterController.triggerToolReturn("vision"), 1700);
    window.setTimeout(() => { walterController.setWalterState("speaking"); walterController.setAudioLevel(.72); }, 3400);
    window.setTimeout(() => { walterController.setAudioLevel(0); walterController.setWalterState("aware"); }, 5700);
  };

  return (
    <main className={`app-shell role-${role} quality-${snapshot.settings.performanceMode} ${kiosk ? "is-kiosk" : ""}`} data-performance={snapshot.settings.performanceMode}>
      <AudioObserver enabled={soundEnabled} />
      {role === "main" && <div className="single-display-stage">{main}</div>}
      {role === "third-eye" && <div className="single-display-stage aux-stage"><ThirdEyeDisplay snapshot={snapshot} /></div>}
      {role === "heart" && <div className="single-display-stage aux-stage"><HeartDisplay snapshot={snapshot} /></div>}
      {role === "simulator" && (
        <section className={`simulator-stage bench-${benchScale} bench-focus-${benchView}`} aria-label="Walter three-screen simulator">
          <nav className="simulator-organ-rail" aria-label="Jump to a Walter display">
            <button className={benchView === "eye" ? "is-active" : ""} onClick={() => setBenchView("eye")}><i />EYE</button>
            <button className={benchView === "face" ? "is-active" : ""} onClick={() => setBenchView("face")}><i />FACE</button>
            <button className={benchView === "heart" ? "is-active" : ""} onClick={() => setBenchView("heart")}><i />HEART</button>
            <button className={benchView === "triptych" ? "is-active" : ""} onClick={() => setBenchView("triptych")}><i />ALL</button>
          </nav>
          <header className="simulator-heading"><div><span>WALTER / EMBODIMENT BENCH</span><strong>THREE INDEPENDENT ORGANS / ONE VERTICAL BODY</strong></div><nav><button className={benchScale === "fit" ? "is-active" : ""} onClick={() => setBenchScale("fit")}>FIT</button><button className={benchScale === "exact" ? "is-active" : ""} onClick={() => setBenchScale("exact")}>1:1</button><button onClick={previewAwakening}>AWAKEN</button><button onClick={previewBodySignal}>PULSE</button></nav></header>
          <div className="simulator-grid">
            <article id="third-eye-bay" className="display-bay bay-third-eye">
              <header className="display-bay-heading">
                <span className="device-online"><i />01 / THIRD EYE</span>
                <b>240 × 240 · SPI</b>
                <a href="/?display=third-eye&kiosk=1" target="_blank" rel="noreferrer">OPEN SOLO ↗</a>
              </header>
              <div className="hardware-shell"><div className="sim-aux"><ThirdEyeDisplay snapshot={snapshot} compact /></div><i className="hardware-port" /></div>
            </article>
            <SignalBridge key={`eye-gap-${snapshot.transfer?.id ?? snapshot.tool.nonce}`} snapshot={snapshot} bridge="eye-face" />
            <article id="presence-bay" className="display-bay bay-main">
              <header className="display-bay-heading">
                <span className="device-online"><i />02 / PRESENCE</span>
                <b>720 × 720 · HDMI / TOUCH</b>
                <a href="/?display=main&kiosk=1" target="_blank" rel="noreferrer">OPEN SOLO ↗</a>
              </header>
              <div className="hardware-shell"><div className="sim-main"><MainDisplay snapshot={snapshot} safeArea={safeArea} onFps={onFps} onTouch={onTouch} compact /></div><i className="hardware-port" /></div>
            </article>
            <SignalBridge key={`heart-gap-${snapshot.transfer?.id ?? snapshot.state}`} snapshot={snapshot} bridge="face-heart" />
            <article id="heart-bay" className="display-bay bay-heart">
              <header className="display-bay-heading">
                <span className="device-online"><i />03 / HEART</span>
                <b>240 × 240 · SPI</b>
                <a href="/?display=heart&kiosk=1" target="_blank" rel="noreferrer">OPEN SOLO ↗</a>
              </header>
              <div className="hardware-shell"><div className="sim-aux"><HeartDisplay snapshot={snapshot} compact /></div><i className="hardware-port" /></div>
            </article>
          </div>
          <div className={`embodiment-flow flow-${snapshot.tool.phase}`} aria-label="Synchronized Walter signal"><span><i />EYE</span><b /><span><i />FACE</span><b /><span><i />HEART</span></div>
          <footer className="simulator-footer"><span>PI 5 TARGET</span><span>LOCAL CHANNEL / {snapshot.state.toUpperCase()}</span><span>3 RENDERERS / 1 MIND</span></footer>
        </section>
      )}
      {!kiosk && (
        <>
          <button className={`sound-toggle ${soundEnabled ? "is-on" : ""}`} onClick={enableSound} aria-pressed={soundEnabled}>
            {soundEnabled ? "SOUND ON" : "SOUND OFF"}
          </button>
          <DeveloperPanel snapshot={snapshot} fps={fps} touch={touch} safeArea={safeArea} onSafeArea={setSafeArea} />
        </>
      )}
    </main>
  );
}
